import React, { useContext, useEffect, useState } from "react";
import { FormPage, TabPanel, ToastType, notifyToast, AppDynamicForm, FormButtonToolbar } from "../../components-old/ComponentsIndex";
import { useSelector } from "react-redux";
import { selectSessionConfig, selectUser } from "../../store/store-selector";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import { handlePostAction } from "../../utils/event-api-invoke";
import {
  QuoteApprovalReassignmentFormData,
  QuoteApprovalReassignmentFormErrorData,
  setQuoteApprovalReassignmentFormFieldContent
} from "./CreateQuoteApprovalReassignmentFormData";
import { AppUtility } from "../../utils/utility";

import _ from "lodash";

const CreateQuoteApprovalReassignmentForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();

  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);
  const user = useSelector(selectUser);

  /**************************************************
  //Form state variables
  **************************************************/
  const [selectedTab, setSelectedTab] = useState("0");
  const [formStateData, setFormStateData] = useState({
    formData: _.cloneDeep(QuoteApprovalReassignmentFormData),
    formError: _.cloneDeep(QuoteApprovalReassignmentFormErrorData),
    disableFormSubmit: true,
    validated: false,
  });

  /**************************************************
  //Event definitions
  **************************************************/

  //updating form state data on any field change
  const setFieldData = (field, val) => {
    try {
      let _fieldData = { ...formStateData.formData };
      let _errorData = { ...formStateData.formError };
      let _enableSubmit = true;
      _fieldData[field].value = val;

      Object.entries(_fieldData).forEach(([key, objProps]) => {
        if (objProps.required) {
          _enableSubmit = _enableSubmit && !!objProps.value;
        }
      });

      if (_errorData[field]) {
        _errorData[field] = null;
        //if date is changed make error as null for both the fields
        if (field === "LeaveStartDate" || field === "LeaveEndDate") {
          _errorData.LeaveStartDate = null;
          _errorData.LeaveEndDate = null;
        }
        setFormStateData((prevState) => ({
          ...prevState,
          formData: _fieldData,
          formError: _errorData,
          disableFormSubmit: !_enableSubmit,
        }));
      } else {
        setFormStateData((prevState) => ({
          ...prevState,
          formData: _fieldData,
          disableFormSubmit: !_enableSubmit,
        }));
      }
    } catch (e) {
      alert(e);
    }
  };
  //on tab change(since tab is a component)
  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);
  };

  //on text field change
  const onFieldValueChange = (e) => {
    try {
      setFieldData(e.target.id, e.target.value);
    } catch (err) {
      alert(err);
    }
  };

  //on date field change
  const onDateValueChange = (id) => (selectedDate) => {
    try {
      let value = selectedDate ? selectedDate.format('YYYY-MM-DD') : "";
      setFieldData(id, value);
    } catch (err) {
      alert(err);
    }
  };

  //field validation
  const validateFields = (fields) => {
    const { LeaveStartDate, LeaveEndDate, CoveringManager } = fields;

    // Validate date range
    const invalidDate = AppUtility.validateDates(LeaveStartDate.value, LeaveEndDate.value);
    if (!invalidDate) {
      return "Leave start date should be less than leave end date";
    }

    //check for logged in user

    if (user.userRecord.email.toLowerCase() === CoveringManager.value.toLowerCase())
      return "Covering manager cannot be logged in user";

    // Validate email format
    const invalidEmail = !AppUtility.validateEmail(CoveringManager.value);
    if (invalidEmail) {
      return "Please enter a valid email address for covering manager";
    }
    return null;
  };

  // Function to map error to fields
  const mapErrorToFields = (error, formError) => {
    const updatedFormError = { ...formError };

    switch (error) {
      case "Leave start date should be less than leave end date":
        updatedFormError.LeaveStartDate = error;
        updatedFormError.LeaveEndDate = error;
        break;
      case "Please enter a valid email address for covering manager":
        updatedFormError.CoveringManager = error;
        break;
      case "Covering manager cannot be logged in user":
        updatedFormError.CoveringManager = error;
        break;
      default:
        break;
    }

    return updatedFormError;
  };

  //on click of save button
  const handleSubmit = async (event) => {
    try {
      if (!setFormStateData.disableFormSubmit) {
        event.preventDefault();
        event.stopPropagation();
        //validate date and email
        let validationError = validateFields(formStateData.formData);
        if (validationError) {
          const updatedFormError = mapErrorToFields(validationError, formStateData.formError);
          setFormStateData((prevData) => ({
            ...prevData,
            formError: updatedFormError,
          }));
          return;
        } else {
          setFormStateData({ ...formStateData, ...{ validated: true } });
          await saveQuoteApprovalReassignment();
        }
      }
    } catch (err) {
      alert(err);
    }
  };

  //on cancel of create screen
  const handleCancel = (e) => {
    try {
      setFormStateData({
        formData: _.cloneDeep(QuoteApprovalReassignmentFormData),
        formError: _.cloneDeep(QuoteApprovalReassignmentFormErrorData),
        disableFormSubmit: true,
        validated: false,
      });
      navigate("/quoteapprovalreassignmentlist");
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form Data & State handler
  **************************************************/

  const FormFieldContent = setQuoteApprovalReassignmentFormFieldContent(
    formStateData.formData,
    onFieldValueChange,
    onDateValueChange,
    formStateData.formError,
    t
  );

  //formatting payload
  const formAPIRequestData = () => {
    try {
      let _data = {
        ConfigId: "",
      };

      Object.entries(formStateData.formData).forEach(([key, objProps]) => {
        _data[key] = objProps.value ? objProps.value : "";

        switch (key) {
          case "LeaveStartDate":
            if (objProps.value) {
              const startDate = new Date(objProps.value);
              startDate.setUTCHours(0, 0, 0, 0); // Set time to beginning of the day
              _data[key] = startDate.toISOString();
            }
            break;
          case "LeaveEndDate":
            if (objProps.value) {
              const endDate = new Date(objProps.value);
              endDate.setUTCHours(23, 59, 59, 999); // Set time to end of the day
              _data[key] = endDate.toISOString();
            }
            break;
          default:
            _data[key] = objProps.value ? objProps.value : "";
            break;
        }

      });

      return [
        {
          data: _data
        },
      ];
    } catch (e) {
      alert(e);
    }
  };

  //API call to save quote approval reassignment
  const saveQuoteApprovalReassignment = async () => {
    try {
      let _apiData = formAPIRequestData();

      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        approvalReassignment: [],
      };
      _apiData.forEach((item) => {
        _postRequest.approvalReassignment.push({
          ...item.data,
          Deleted: false,
          CountryCode: settingsConfig.countryCode,
          BusinessCode: settingsConfig.businessCode,
          SalesUser: user.userRecord.email
        });
      });

      spinnerCtx.setLoadingIndicator(true);
      let res = await handlePostAction(_postRequest, "approvalcoveringmanagerconfig");
      if (res && res.status && res.status === 200) {
        if (res.data && res.data.response && res.data.response.errorNumber) {
          const errorMessage = res.data.response.errorDescription || "Quote approval reassignment creation failed";
          notifyToast(errorMessage, ToastType.ERROR);
          spinnerCtx.setLoadingIndicator(false);
        } else {
          notifyToast("Quote approval reassignment added successfully", ToastType.SUCCESS);
          handleCancel();
        }
      } else {
        notifyToast("Quote approval reassignment creation failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form on load useEffect
  **************************************************/

  useEffect(() => {
    const initFormComponents = () => {
      setFormStateData({
        ...formStateData,
      });
      spinnerCtx.setLoadingIndicator(false);
    };

    spinnerCtx.setLoadingIndicator(true);
    initFormComponents();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage>
      <TabPanel
        tabs={[
          {
            icon: "",
            label: t("quoteApprovalReassignmentList.header"),
            children: (
              <>
                <div style={{ fontWeight: "bold", height: "30px" }}>
                  {t("quoteApprovalReassignmentList.header")}
                </div>
                <AppDynamicForm formField={FormFieldContent} handleSubmit={handleSubmit} validated={formStateData.validated} />
                <FormButtonToolbar
                  onCancelClick={handleCancel}
                  onSubmitClick={handleSubmit}
                  isSubmitDisabled={formStateData.disableFormSubmit}
                  byPassRoleCheck={true}
                  buttonText={t("generic.save")}
                />
              </>
            ),
          },
        ]}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
      />
    </FormPage>
  );
};

export default CreateQuoteApprovalReassignmentForm;
